import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useUpdateMapping } from '../api/providers.queries';
import { toFormPath, toFormValues, toMappingPayload } from '../lib/mapping-form';
import { mapProvidersSchema, type MapProvidersValues } from '../schemas/mapping.schema';
import type { Mapping } from '../types/provider.types';
import { MintrouteFields } from './mintroute-fields';

/**
 * Change a mapping's cost, product id and — for Mintroute — its catalogue
 * details. Its provider and denomination never change. New mappings are added
 * with `MapProvidersDialog`; both use the same row shape (`items[0]` here).
 * Mount only while open.
 */
export function MappingFormDialog({
  mapping,
  denominationLabel,
  onClose,
}: {
  mapping: Mapping;
  denominationLabel: string;
  onClose: () => void;
}) {
  const update = useUpdateMapping();

  const form = useForm<MapProvidersValues>({
    resolver: zodResolver(mapProvidersSchema),
    defaultValues: toFormValues(mapping),
  });

  const { error } = update;
  useEffect(() => {
    if (!(error instanceof ApiError)) return;
    for (const [field, message] of Object.entries(error.fieldErrors ?? {})) {
      const path = toFormPath(field);
      if (path) form.setError(path, { type: 'server', message });
    }
  }, [error, form]);

  const onSubmit = (values: MapProvidersValues) => {
    const [item] = values.items;
    if (!item) return;
    update.mutate(
      { mappingId: mapping.id, payload: toMappingPayload(item) },
      { onSuccess: onClose },
    );
  };

  const isPending = update.isPending;
  const isMintroute = mapping.integration === 'Mintroute';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={isMintroute ? 'max-h-[90vh] overflow-y-auto sm:max-w-2xl' : 'sm:max-w-lg'}
      >
        <DialogHeader>
          <DialogTitle className="text-base">{`Edit ${mapping.vendorName ?? 'mapping'}`}</DialogTitle>
          <DialogDescription className="text-xs">{denominationLabel}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="items.0.cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Cost{mapping.costCurrency ? ` (${mapping.costCurrency})` : ''}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={Number.isNaN(field.value) ? '' : field.value}
                        onChange={(event) => field.onChange(event.target.valueAsNumber)}
                        type="number"
                        min={0}
                        step="0.0001"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormDescription>
                      What the provider charges us. The cheapest usable provider is chosen.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="items.0.productIdentifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{mapping.productIdentifierLabel}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        inputMode={mapping.productIdentifierRequired ? 'numeric' : undefined}
                        className="font-mono text-xs"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormDescription>
                      {mapping.productIdentifierRequired
                        ? `Required: orders are bought from ${mapping.vendorName ?? 'this provider'} with it.`
                        : 'Optional for this provider.'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isMintroute && (
                <MintrouteFields
                  control={form.control}
                  index={0}
                  names={{
                    category: mapping.categoryName,
                    brand: mapping.brandName,
                    denomination: mapping.denominationName,
                  }}
                  disabled={isPending}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !form.formState.isDirty}>
                {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Save changes
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
